/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { FC, useEffect, useState, useMemo } from "react"
import { useSelector, useDispatch } from "react-redux"
import { AppState } from "store/store.type"
import { BaseDepartment, Department } from "types/models/menu"
import { SortDrawer } from "components/drawers/SortDrawer"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"
import { MenuOverviewRow } from "components/common"
import { useTranslation, useTranslationsMap } from "common/utils/i18n"
import { sortMenuItemNewLogicWithSortedIndexes } from "utils/sort_helper"
import { useEnablePartialMenu } from "hooks/useEnablePartialMenu"
import {
  flattenInfiniteHierarchies,
  useMenuV3HierarchiesInfinite,
  useSortDepartmentsMutation,
} from "queries"
import { FoodmaxapipbEntityType } from "generated"
import { mapHierarchyEntitiesToSubDepartments } from "utils/menuV3Mapper"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import { fetchSortDepartments } from "utils/rtk_migration_helper"

const rowHeight = 64

interface SortSubDepartmentsDrawerProps {
  visible: boolean
  onClose(): void
}

export const SortSubDepartmentsDrawer: FC<SortSubDepartmentsDrawerProps> = ({
  visible,
  onClose,
}: SortSubDepartmentsDrawerProps) => {
  const dispatch = useDispatch()
  const [sortedIndexes, setSortedIndexes] = useState<[number, number][]>([])
  const [sortDepartmentsRtk] = useSortDepartmentsMutation()
  const {
    flags: { sortDepartments: isRtkSortDepartmentsEnabled },
    isLoading: isRtkFlagLoading,
  } = useRtkQueryMigrationEndpointsFlag()

  const {
    merchantID,
    overview: {
      department: parentDepartment,
      subDepartments: reduxSubDepartments,
    },
    isMartDepartmentStructure,
    isSellingTimeStructure,
    merchantGroupID,
    menuGroupDetail: { menuGroupID },
    isUCS,
  } = useSelector((state: AppState) => state.menu)

  const enablePartialMenu = useEnablePartialMenu()

  // Fetch sub-departments from query when partial menu is enabled
  const { currentData: hierarchiesData } = useMenuV3HierarchiesInfinite(
    {
      parentType: FoodmaxapipbEntityType.Department,
      parentID: parentDepartment?.itemClassID,
      merchantID,
      merchantGroupID,
    },
    {
      skip: !enablePartialMenu || !parentDepartment?.itemClassID || !merchantID,
    },
  )

  // Use query sub-departments when partial menu is enabled, otherwise use Redux sub-departments
  const subDepartments = useMemo(() => {
    if (enablePartialMenu && hierarchiesData && parentDepartment?.itemClassID) {
      const allEntities = flattenInfiniteHierarchies(hierarchiesData)
      return mapHierarchyEntitiesToSubDepartments(
        allEntities || [],
        parentDepartment.itemClassID,
        parentDepartment.itemClassName,
      )
    }
    return reduxSubDepartments
  }, [
    enablePartialMenu,
    hierarchiesData,
    parentDepartment,
    reduxSubDepartments,
  ])

  useEffect(() => {
    setSortedIndexes([])
  }, [subDepartments])

  const textMap = useTranslationsMap({
    sortCategoriesSuccess: "menu.notifications.sortCategories.successMessage",
  })

  const onSortEnd = (sortedDepartments: Department[]) => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewRearrangeSubDepartment,
      action: EventAction.submitRearrangeSubDepartment,
      labels: {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        CATEGORY_ID: parentDepartment?.itemClassID ?? "",
        IS_PARTIAL_MENU: enablePartialMenu,
      },
    })

    let sorts
    if (enablePartialMenu) {
      sorts = sortedDepartments.map((department, index) => ({
        resourceID: department.itemClassID ?? "",
        sortOrder: subDepartments[index].sortOrder ?? 0,
      }))
    } else if (isUCS) {
      sorts = sortMenuItemNewLogicWithSortedIndexes({
        originalList: subDepartments,
        sortedIndexes,
        sortKey: "sortOrder",
        itemKey: "itemClassID",
      })
    } else {
      sorts = sortedDepartments.map((department, index) => ({
        resourceID: department.itemClassID ?? "",
        sortOrder: sortedDepartments.length - index,
      }))
    }

    // While migration flags are loading, do not fall back to old Redux: the enabled bit is
    // unknown until GrabX resolves. Legacy-then-RTK for the same user intent can double-call
    // APIs/middleware and splits RTK migration tracking. Wait for !isRtkFlagLoading; disable
    // the trigger UI while loading so clicks are not silently dropped.
    if (!isRtkFlagLoading) {
      fetchSortDepartments({
        dispatch,
        merchantID,
        merchantGroupID,
        menuGroupID,
        parentDepartmentID: parentDepartment?.itemClassID,
        sorts,
        successMessage: textMap.sortCategoriesSuccess,
        isMartDepartmentStructure,
        isSellingTimeStructure,
        enablePartialMenu,
        isRtkSortDepartmentsEnabled,
        sortDepartmentsRtk,
      })
    }
  }

  return (
    <SortDrawer
      title={useTranslation("menu.drawers.sort.subCategories")}
      items={subDepartments}
      visible={visible}
      onClose={() => {
        TroyWebTracker.sendEvent({
          category: EventCategory.menuOverviewRearrangeSubDepartment,
          action: EventAction.clickExit,
          labels: {
            ...(merchantID
              ? { STORE_ID: merchantID }
              : {
                  MERCHANT_GROUP_ID: merchantGroupID,
                  MENU_GROUP_ID: menuGroupID,
                }),
            CATEGORY_ID: parentDepartment?.itemClassID ?? "",
            IS_PARTIAL_MENU: enablePartialMenu,
          },
        })
        onClose()
      }}
      onSave={onSortEnd}
      saveDisabled={isRtkFlagLoading}
      itemSize={rowHeight}
      renderRowContent={(department: BaseDepartment) => (
        <MenuOverviewRow
          name={department.itemClassName ?? ""}
          count={department.totalItems ?? 0}
          countNotAvailable={department.totalUnavailableItems}
        />
      )}
      onSort={({ oldIndex, newIndex }) => {
        TroyWebTracker.sendEvent({
          category: EventCategory.menuOverviewRearrangeSubDepartment,
          action: EventAction.rearrangeSubDepartment,
          labels: {
            ...(merchantID
              ? { STORE_ID: merchantID }
              : {
                  MERCHANT_GROUP_ID: merchantGroupID,
                  MENU_GROUP_ID: menuGroupID,
                }),
            CATEGORY_ID: parentDepartment?.itemClassID ?? "",
            IS_PARTIAL_MENU: enablePartialMenu,
          },
        })
        if (isUCS) {
          setSortedIndexes(prevState => [...prevState, [oldIndex, newIndex]])
        }
      }}
      onClear={() => {
        setSortedIndexes([])
      }}
      data-testid="sort-department-drawer"
    />
  )
}
